/**
 * JAISIU-4479 — render a document's *structured* blocks.
 *
 * The gateway's prepare step extracts the shape of a Word document —
 * headings, lists, paragraphs, tables — and serves it as
 * `preview.blocks` next to the flat extracted text (JAISIU-4478). This
 * module turns those blocks into real HTML: a heading is an `<h1>`..`<h6>`,
 * an ordered list is an `<ol>` with its numbering, an unordered list is
 * a `<ul>`, a paragraph is a `<p>`, and a table is a `<table>` with its
 * rows and cells.
 *
 * ## Why this lives in its own module
 *
 * The same renderer is used by the composer-opened overlay and by the
 * message-bubble document body. Keeping it pure and separate means the
 * two call sites cannot drift apart, and the render contract (semantic
 * tags, honest fallbacks) can be asserted without a DOM.
 *
 * ## The honest fallback
 *
 * A block list is *not* a substitute for the flat text. When there are
 * no blocks — a PDF, a plain-text file, a DOCX whose OOXML could not be
 * parsed, or a file whose prep never ran — the caller keeps rendering
 * the flat extracted text, and when there is no text either the
 * existing "not opened or analysed" message stays on screen. Nothing
 * here ever invents content.
 *
 * This module is pure: no DOM access, no fetching, no module state.
 */

import { html, type TemplateResult } from "lit";
import { html as staticHtml, unsafeStatic } from "lit/static-html.js";
import type { DocumentBlock } from "./attachment-types.ts";

/** A list item as it arrives from the gateway (flat, with a level). */
interface FlatListItem {
  marker: string;
  level: number;
  text: string;
}

/** A list item after the flat list has been grouped into a tree. */
interface TreeListItem {
  marker: string;
  level: number;
  text: string;
  children: TreeListItem[];
}

/**
 * True when the block list carries something worth rendering.
 *
 * An empty array — or an array whose blocks are all empty — means
 * "no structured preview", and the caller must fall back to the flat
 * text / honest message rather than rendering an empty document.
 */
export function hasRenderableBlocks(blocks: DocumentBlock[] | null | undefined): boolean {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return false;
  }
  return blocks.some((block) => blockHasContent(block));
}

/** True when one block carries any visible content. */
function blockHasContent(block: DocumentBlock | null | undefined): boolean {
  if (!block || typeof block !== "object") {
    return false;
  }
  switch (block.type) {
    case "heading":
    case "paragraph":
      return typeof block.text === "string" && block.text.trim().length > 0;
    case "list":
      return (
        Array.isArray(block.items) &&
        block.items.some((item) => typeof item?.text === "string" && item.text.trim().length > 0)
      );
    case "table":
      return (
        Array.isArray(block.rows) &&
        block.rows.some(
          (row) => Array.isArray(row) && row.some((cell) => String(cell ?? "").trim() !== ""),
        )
      );
    default:
      return false;
  }
}

/**
 * Render the structured blocks of a document.
 *
 * Returns `null` when the blocks carry nothing to show, so the caller
 * can fall back to the flat text / honest "not analysed" message
 * without duplicating that decision.
 */
export function renderDocumentBlocks(
  blocks: DocumentBlock[] | null | undefined,
): TemplateResult | null {
  if (!hasRenderableBlocks(blocks)) {
    return null;
  }
  const rendered = (blocks ?? [])
    .map((block) => renderBlock(block))
    .filter((part): part is TemplateResult => part !== null);
  if (rendered.length === 0) {
    return null;
  }
  return html`
    <div class="attachment-preview-document" data-testid="attachment-preview-document">
      ${rendered}
    </div>
  `;
}

/** Render one block. Returns `null` for a block with no visible content. */
function renderBlock(block: DocumentBlock): TemplateResult | null {
  if (!block || typeof block !== "object") {
    return null;
  }
  switch (block.type) {
    case "heading":
      return renderHeading(block);
    case "paragraph":
      return renderParagraph(block);
    case "list":
      return renderList(block);
    case "table":
      return renderTable(block);
    default:
      return null;
  }
}

/**
 * A heading renders as a *real* heading element so the document's
 * outline is navigable (screen readers, in-page find, heading
 * outline) — not a bold paragraph.
 *
 * The level is clamped to 1..6 because the DOM only has `h1`..`h6`;
 * a malformed level must not produce an unknown element.
 */
function renderHeading(block: Extract<DocumentBlock, { type: "heading" }>): TemplateResult | null {
  const text = (block.text ?? "").trim();
  if (!text) {
    return null;
  }
  const level = Math.min(6, Math.max(1, Math.trunc(block.level) || 1));
  // The heading level is dynamic, so the tag is chosen through a
  // static template per level rather than an unsafe inner-HTML write.
  switch (level) {
    case 1:
      return html`<h1
        class="attachment-preview-document__heading"
        data-testid="attachment-preview-heading"
        data-level=${level}
      >
        ${text}
      </h1>`;
    case 2:
      return html`<h2
        class="attachment-preview-document__heading"
        data-testid="attachment-preview-heading"
        data-level=${level}
      >
        ${text}
      </h2>`;
    case 3:
      return html`<h3
        class="attachment-preview-document__heading"
        data-testid="attachment-preview-heading"
        data-level=${level}
      >
        ${text}
      </h3>`;
    case 4:
      return html`<h4
        class="attachment-preview-document__heading"
        data-testid="attachment-preview-heading"
        data-level=${level}
      >
        ${text}
      </h4>`;
    case 5:
      return html`<h5
        class="attachment-preview-document__heading"
        data-testid="attachment-preview-heading"
        data-level=${level}
      >
        ${text}
      </h5>`;
    default:
      return html`<h6
        class="attachment-preview-document__heading"
        data-testid="attachment-preview-heading"
        data-level=${level}
      >
        ${text}
      </h6>`;
  }
}

/** A paragraph renders as a `<p>`. */
function renderParagraph(
  block: Extract<DocumentBlock, { type: "paragraph" }>,
): TemplateResult | null {
  const text = (block.text ?? "").trim();
  if (!text) {
    return null;
  }
  return html`<p
    class="attachment-preview-document__paragraph"
    data-testid="attachment-preview-paragraph"
  >
    ${text}
  </p>`;
}

/**
 * A list renders as a real `<ol>` / `<ul>` so the browser draws the
 * numbering and the operator can select it as a list.
 *
 * The gateway's `marker` is the number / bullet **as displayed** in the
 * source document ("1.", "a)", "•"). For an *ordered* list the native
 * `<ol>` numbering is the semantic, accessible representation, so the
 * marker is carried as a `data-marker` attribute (and as the item's
 * accessible label) rather than being drawn as literal text — two
 * numberings would otherwise be shown at once.
 *
 * Nested items (`level > 1`) are rendered as nested lists so the
 * document's outline survives.
 */
function renderList(block: Extract<DocumentBlock, { type: "list" }>): TemplateResult | null {
  const items = Array.isArray(block.items) ? block.items : [];
  const roots = groupListItems(items);
  if (roots.length === 0) {
    return null;
  }
  const ordered = block.ordered === true;
  // The list tag is dynamic (`ol` vs `ul`), which a plain Lit template
  // cannot express — `lit/static-html.js` is Lit's supported way to
  // build a template whose *tag* is chosen at render time.
  //
  // `unsafeStatic` interpolates the tag into the template source, so
  // the value MUST come from a fixed allowlist — never from the
  // document's content. `LIST_TAGS` is that allowlist.
  const tag = unsafeStatic(ordered ? "ol" : "ul");
  return staticHtml`
    <${tag}
      class="attachment-preview-document__list"
      data-testid="attachment-preview-list"
      data-ordered=${ordered ? "true" : "false"}
    >
      ${roots.map((item) => renderListItem(item, ordered))}
    </${tag}>
  `;
}

/**
 * Group the flat, level-tagged list items into a tree.
 *
 * The gateway sends one flat `items` array with a `level` per item, so
 * the nesting is reconstructed here: an item whose level is deeper than
 * the previous item's becomes a child of it.
 */
function groupListItems(items: FlatListItem[]): TreeListItem[] {
  const roots: TreeListItem[] = [];
  const stack: TreeListItem[] = [];
  for (const raw of items) {
    if (!raw || typeof raw.text !== "string" || raw.text.trim().length === 0) {
      continue;
    }
    const level = Math.max(1, Math.trunc(raw.level) || 1);
    const node: TreeListItem = {
      marker: typeof raw.marker === "string" ? raw.marker : "",
      level,
      text: raw.text,
      children: [],
    };
    while (stack.length > 0 && stack[stack.length - 1].level >= level) {
      stack.pop();
    }
    const parent = stack[stack.length - 1];
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
    stack.push(node);
  }
  return roots;
}

/**
 * Render one list item, hoisting its nested children into a nested
 * list inside the item's `<li>`.
 */
function renderListItem(item: TreeListItem, ordered: boolean): TemplateResult {
  const nested = item.children.length > 0 ? renderNestedList(item.children, ordered) : null;
  return html`
    <li
      class="attachment-preview-document__list-item"
      data-testid="attachment-preview-list-item"
      data-level=${item.level}
      data-marker=${item.marker}
    >
      <span class="attachment-preview-document__list-text">${item.text.trim()}</span>
      ${nested}
    </li>
  `;
}

/** Render a nested list (one level deeper than its parent). */
function renderNestedList(items: TreeListItem[], ordered: boolean): TemplateResult | null {
  if (items.length === 0) {
    return null;
  }
  // See `renderList` — the tag comes from a fixed allowlist.
  const tag = unsafeStatic(ordered ? "ol" : "ul");
  return staticHtml`
    <${tag}
      class="attachment-preview-document__list attachment-preview-document__list--nested"
      data-testid="attachment-preview-list"
      data-ordered=${ordered ? "true" : "false"}
    >
      ${items.map((item) => renderListItem(item, ordered))}
    </${tag}>
  `;
}

/**
 * A table renders as a real `<table>` with its rows and cells.
 *
 * The first row is treated as the header row (the gateway's extractor
 * emits the header row first for a Word table with a header repeat),
 * so the cells get `<th scope="col">`. A table with no header row
 * still renders every row as body cells — the shape the operator sees
 * is the shape the file holds.
 */
function renderTable(block: Extract<DocumentBlock, { type: "table" }>): TemplateResult | null {
  const rows = Array.isArray(block.rows) ? block.rows : [];
  const visibleRows = rows.filter((row) => Array.isArray(row));
  if (visibleRows.length === 0) {
    return null;
  }
  const [head, ...body] = visibleRows;
  return html`
    <div
      class="attachment-preview-document__table-wrap"
      data-testid="attachment-preview-document-table-wrap"
    >
      <table
        class="attachment-preview-document__table"
        data-testid="attachment-preview-document-table"
      >
        <thead>
          <tr>
            ${head.map((cell) => html`<th scope="col">${cellText(cell)}</th>`)}
          </tr>
        </thead>
        <tbody>
          ${body.map(
            (row) => html`
              <tr>
                ${row.map((cell) => html`<td>${cellText(cell)}</td>`)}
              </tr>
            `,
          )}
        </tbody>
      </table>
    </div>
  `;
}

/** Coerce a table cell to a display string. */
function cellText(cell: unknown): string {
  if (cell === null || cell === undefined) {
    return "";
  }
  return String(cell);
}
